import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import * as React from 'react';

import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/catalyst/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface DatePickerProps {
  date?: Date;
  onDateChange: (date: Date | undefined) => void;
  placeholder?: string;
  className?: string;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'right' | 'bottom' | 'left';
}

export const DatePicker = React.memo<DatePickerProps>(
  ({
    date,
    onDateChange,
    placeholder = 'Pick a date',
    className,
    align = 'center',
    side = 'bottom',
  }) => {
    const [open, setOpen] = React.useState(false);

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            outline
            className={cn(
              'justify-start text-left font-normal flex items-center justify-center whitespace-nowrap',
              !date && 'text-muted-foreground',
              className,
            )}
            style={{ isolation: 'auto', position: 'unset' }}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {date ? format(date, 'MMM d, yyyy') : placeholder}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align={align} side={side}>
          <Calendar
            defaultMonth={date}
            mode="single"
            selected={date}
            onSelect={(newDate) => {
              onDateChange(newDate);
              setOpen(false);
            }}
            initialFocus
          />
          {date && (
            <div className="p-3 border-t">
              <Button
                outline
                className="w-full"
                onClick={() => {
                  onDateChange(undefined);
                  setOpen(false);
                }}
              >
                Clear
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    );
  },
);

DatePicker.displayName = 'DatePicker';
